/**
 * Composite Analyzer: In-Trade Counter-Signal (Phase 19).
 *
 * Reads `trade.barAnalysis.counterSignals` (populated at backfill time by
 * detectInTradeCounterSignals) and surfaces patterns like:
 *
 *   "When '15m close vs 9 EMA' fires on your shorts, WR drops from 53% to 28%
 *    across 12 trades. You ignored it 9/12 times — net −$420 on those.
 *    You exited within 5 minutes of it firing 3/12 times — saved ~$180.
 *    When this signal fires next, exit."
 *
 * Methodology:
 *
 *   1. For each setup (or globally if no setup scope), bucket trades two
 *      ways for each unique signal signature:
 *        - "fired" cohort   — trades where the signal fired
 *        - "didn't fire"    — trades where the signal didn't (baseline)
 *
 *   2. Compute WR + avg R per cohort. The gap is the signal's predictive
 *      value: a big WR drop in the "fired" cohort means the signal IS a
 *      reliable counter-indicator.
 *
 *   3. Within the "fired" cohort, classify behavior:
 *        - exited-on-signal    — trade closed within EXIT_WINDOW_SEC of signal
 *        - held-through        — trade continued past EXIT_WINDOW_SEC
 *      This lets the action text be specific: did exiting save money?
 *
 *   4. Only emit when:
 *        - signal fired on ≥MIN_TRADES_FIRED trades (typically 4)
 *        - baseline cohort has ≥MIN_TRADES_BASELINE trades (typically 4)
 *        - WR gap ≥MIN_WR_GAP (typically 15pp) OR avg R gap ≥MIN_R_GAP
 *
 * Reads:
 *   trade.barAnalysis.counterSignals — from counterSignals.detector
 *   trade.netPnl                     — must be enriched by scope
 *   trade.rMultiple                  — for avg R
 *   trade.exitTime + trade.date      — for exit-window classification
 */

import { CATEGORY, SEVERITY, ACTION_TYPE, makeObservation, makeAction } from '../../types.js';
import { signalSignature, signalLabel } from '../../../barAnalysis/counterSignals.js';
import { getExecutionInstantMs } from '../../../timeService.js';

export const key = 'inTradeCounterSignal';
export const tier = 'free';

// Sample-size guards. Counter-signals fire rarely; lower bars than other
// analyzers so we can surface patterns earlier in the trader's history.
const MIN_TRADES_FIRED = 4;        // signal must have fired this many times
const MIN_TRADES_BASELINE = 4;     // baseline cohort needs this many too
const MIN_WR_GAP = 0.15;           // 15pp WR drop = noteworthy
const MIN_R_GAP = 0.4;             // 0.4R avg R drop = noteworthy

// A trade is classified as "exited on signal" if its exit timestamp is
// within this many seconds AFTER the signal fired. 5 minutes catches
// "saw the bar close, acted in the next minute or two" behavior while
// excluding coincidental exits from natural stop-outs much later.
const EXIT_WINDOW_SEC = 300;

const fmtPct = (n) => `${Math.round(n * 100)}%`;
const fmtR = (n) => Number.isFinite(n) ? `${n >= 0 ? '+' : ''}${n.toFixed(2)}R` : '—';
const fmtUsd = (n) => {
  if (!Number.isFinite(n)) return '—';
  const sign = n >= 0 ? '+' : '−';
  return `${sign}$${Math.abs(n).toFixed(0)}`;
};

const isWin = (t) => Number.isFinite(t.netPnl) && t.netPnl > 0;

const cohortStats = (trades) => {
  const wins = trades.filter(isWin).length;
  const wr = trades.length > 0 ? wins / trades.length : 0;
  const rs = trades.map(t => parseFloat(t.rMultiple)).filter(Number.isFinite);
  const avgR = rs.length > 0 ? rs.reduce((s, r) => s + r, 0) / rs.length : null;
  const netPnl = trades.reduce((s, t) => s + (Number.isFinite(t.netPnl) ? t.netPnl : 0), 0);
  return { count: trades.length, wins, wr, avgR, netPnl };
};

/**
 * Resolve a trade's exit timestamp to Unix seconds, or null if not closed.
 * Uses the same getExecutionInstantMs helper as the detector + maeMfe so
 * the cross-reference comparison is exact.
 */
const exitTsOf = (trade) => {
  if (!trade?.exitTime || !trade?.date) return null;
  const ms = getExecutionInstantMs(trade.date, trade.exitTime);
  return ms ? Math.floor(ms / 1000) : null;
};

/**
 * For a given trade + signal event, classify how the trader reacted:
 *   'exited' — exit happened within EXIT_WINDOW_SEC of the signal
 *   'held'   — trade continued well past the signal (or still open)
 * Returns 'unknown' if we can't determine (no exit time).
 */
const classifyReaction = (trade, event) => {
  const exitTs = exitTsOf(trade);
  if (exitTs == null) return 'unknown';
  const dt = exitTs - event.barTime;
  if (dt < 0) return 'unknown';  // exit BEFORE signal? shouldn't happen, treat as undefined
  if (dt <= EXIT_WINDOW_SEC) return 'exited';
  return 'held';
};

/**
 * Build the cohort buckets for one signal signature.
 * Returns { fired: trade[], baseline: trade[], firedEvents: Map<tradeId, event[]> }
 */
const bucketBySignal = (trades, signature) => {
  const fired = [];
  const baseline = [];
  const firedEvents = new Map();

  for (const t of trades) {
    const events = Array.isArray(t.barAnalysis?.counterSignals) ? t.barAnalysis.counterSignals : null;
    if (!events) {
      // Trade hasn't been backfilled — exclude from analysis entirely.
      // Including it in baseline would be a lie ("we know this signal
      // didn't fire") when we just have no data.
      continue;
    }
    const matching = events.filter(e => signalSignature(e) === signature);
    if (matching.length > 0) {
      fired.push(t);
      firedEvents.set(t.id, matching);
    } else {
      baseline.push(t);
    }
  }
  return { fired, baseline, firedEvents };
};

/**
 * Build the action sentence based on the exit-behavior split inside the
 * "fired" cohort. Two intent variants:
 *   - "Exits saved money" — exit cohort net > held cohort avg net
 *   - "Holds bled money"  — held cohort heavily negative
 * Either way, the action is "exit when this fires" — but the framing
 * adapts to the actual data so it reads as observed, not preached.
 */
const buildActionText = (signalLabelStr, firedCohort, firedEvents) => {
  const exited = [];
  const held = [];
  for (const t of firedCohort) {
    const events = firedEvents.get(t.id) || [];
    if (events.length === 0) continue;
    // Use the FIRST signal event (earliest) — that's the moment the trader
    // could have first reacted. Later signals are persistence.
    const reaction = classifyReaction(t, events[0]);
    if (reaction === 'exited') exited.push(t);
    else if (reaction === 'held') held.push(t);
    // 'unknown' falls through (open trades or no exit time)
  }
  if (exited.length === 0 && held.length === 0) {
    return `When ${signalLabelStr} fires next, exit. The data says it's a real counter-signal.`;
  }
  if (exited.length > 0 && held.length > 0) {
    const exitedStats = cohortStats(exited);
    const heldStats = cohortStats(held);
    return `When you exited within 5m of ${signalLabelStr}: ${exited.length} trade${exited.length > 1 ? 's' : ''} net ${fmtUsd(exitedStats.netPnl)}. When you held through: ${held.length} trade${held.length > 1 ? 's' : ''} net ${fmtUsd(heldStats.netPnl)}. The data is clear — exit on this signal.`;
  }
  if (held.length > 0) {
    const heldStats = cohortStats(held);
    return `You held through ${held.length}/${firedCohort.length} times when this fired — net ${fmtUsd(heldStats.netPnl)} on those. Next time, exit within 5 minutes of the bar close.`;
  }
  return `You've exited on this signal ${exited.length}/${firedCohort.length} times. Keep doing that.`;
};

/**
 * Find ALL unique signal signatures present across the trades, then run
 * the cohort analysis per signature. Returns observations for whichever
 * signatures meet the thresholds.
 */
const analyzeAllSignaturesForScope = (trades, scopeId, scope) => {
  if (!Array.isArray(trades) || trades.length === 0) return [];

  // Collect unique signatures + a count of how many trades each appeared on.
  const sigCounts = new Map();
  for (const t of trades) {
    const events = Array.isArray(t.barAnalysis?.counterSignals) ? t.barAnalysis.counterSignals : null;
    if (!events) continue;
    // Dedupe within a single trade — one signature can only count once per trade.
    const uniqHere = new Set(events.map(signalSignature).filter(Boolean));
    for (const sig of uniqHere) {
      sigCounts.set(sig, (sigCounts.get(sig) || 0) + 1);
    }
  }
  if (sigCounts.size === 0) return [];

  const observations = [];

  for (const [signature, firedCount] of sigCounts.entries()) {
    if (firedCount < MIN_TRADES_FIRED) continue;

    const { fired, baseline, firedEvents } = bucketBySignal(trades, signature);
    if (fired.length < MIN_TRADES_FIRED) continue;
    if (baseline.length < MIN_TRADES_BASELINE) continue;

    const firedStats = cohortStats(fired);
    const baselineStats = cohortStats(baseline);

    const wrGap = baselineStats.wr - firedStats.wr;
    const rGap = (baselineStats.avgR ?? 0) - (firedStats.avgR ?? 0);

    // Both gaps need to be MEANINGFUL — but either WR or R alone qualifies
    // (sometimes a signal kills your R-multiple without crushing your WR,
    // or vice versa, both are worth surfacing).
    if (wrGap < MIN_WR_GAP && rGap < MIN_R_GAP) continue;

    // Use the first event from the first fired trade to derive a label.
    // All events with the same signature share label-relevant fields.
    const firstEvents = firedEvents.get(fired[0].id) || [];
    if (firstEvents.length === 0) continue;
    const label = signalLabel(firstEvents[0]);

    const scopeWord = scope === 'setup' ? ` on "${scopeId}"` : '';
    const obsId = `${key}:${scope}:${scopeId}:${signature}`;

    // Structured actions — one-click commands on the observation card.
    // The user has three reasonable responses to a counter-signal pattern:
    //   1. Add it as a rule on the setup (formalize "exit when X fires")
    //   2. Mute it for a week (they've noted it; don't keep nagging)
    //   3. Pin to lessons (long-term reflection)
    // ADD_RULE_TO_SETUP only makes sense at setup scope (no setupName at
    // composite/global scope); composite scopes get the mute path only.
    const actions = [];
    if (scope === 'setup') {
      actions.push(makeAction({
        type: ACTION_TYPE.ADD_RULE_TO_SETUP,
        label: 'Add as exit rule',
        payload: {
          setupName: scopeId,
          rule: `Exit when ${label} fires`,
        },
        confirmText: `Add "Exit when ${label} fires" as a rule on "${scopeId}"?`,
        doneLabel: 'Added ✓',
      }));
    }
    actions.push(makeAction({
      type: ACTION_TYPE.MUTE_OBSERVATION,
      label: 'Mute 7 days',
      payload: {
        observationId: obsId,
        days: 7,
      },
      doneLabel: 'Muted ✓',
    }));

    observations.push(makeObservation({
      id: obsId,
      analyzer: key,
      scope,
      scopeId,
      severity: SEVERITY.WARNING,
      category: CATEGORY.EXECUTION,
      title: `"${label}" is a counter-signal${scopeWord} — WR ${fmtPct(baselineStats.wr)} → ${fmtPct(firedStats.wr)} when it fires`,
      detail: `${fired.length} trade${fired.length > 1 ? 's' : ''} where this fired: ${firedStats.wins}/${firedStats.count} winners (${fmtPct(firedStats.wr)}${firedStats.avgR != null ? `, avg ${fmtR(firedStats.avgR)}` : ''}, net ${fmtUsd(firedStats.netPnl)}). Baseline (${baseline.length} trade${baseline.length > 1 ? 's' : ''} without): ${fmtPct(baselineStats.wr)}${baselineStats.avgR != null ? `, avg ${fmtR(baselineStats.avgR)}` : ''}, net ${fmtUsd(baselineStats.netPnl)}.`,
      action: buildActionText(label, fired, firedEvents),
      actions,
      evidence: {
        signature,
        firedCount: fired.length,
        baselineCount: baseline.length,
        firedWR: firedStats.wr,
        baselineWR: baselineStats.wr,
        firedAvgR: firedStats.avgR,
        baselineAvgR: baselineStats.avgR,
        firedNetPnl: firedStats.netPnl,
        baselineNetPnl: baselineStats.netPnl,
        wrGap,
        rGap,
      },
      confidence: 0.75,
      priority: 84,
    }));
  }

  return observations;
};

/**
 * Setup-scope entry point. The composite analyzer is dispatched by the
 * setup scope (which provides setupName + setupTrades) — the same shape
 * as convictionCalibration, atmBracketCalibration, etc.
 */
export const analyze = (ctx) => {
  const { setupName, setupTrades } = ctx || {};
  if (!setupName || !Array.isArray(setupTrades) || setupTrades.length === 0) return null;

  // Live-trade gate: this analyzer compares fired-signal cohort WR/R against a
  // baseline cohort. Open trades have unrealized P&L which isn't a realized
  // outcome — including them would fire premature "this signal is killing
  // your WR" claims while the user is still in the trade. Matches
  // summarizePnl semantics.
  const closedSetupTrades = setupTrades.filter(t => t?.isOpen !== true);
  if (closedSetupTrades.length === 0) return null;

  const obs = analyzeAllSignaturesForScope(closedSetupTrades, setupName, 'setup');
  return obs.length > 0 ? obs : null;
};

// Test-friendly exports
export {
  cohortStats,
  bucketBySignal,
  classifyReaction,
  exitTsOf,
  analyzeAllSignaturesForScope,
  MIN_TRADES_FIRED,
  MIN_TRADES_BASELINE,
  MIN_WR_GAP,
  MIN_R_GAP,
  EXIT_WINDOW_SEC,
};
